import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { apiClient } from '../services/api';
import { formatCurrency } from '../utils/formatCurrency';
import WarningsPanel from './WarningsPanel';
import type {
  PreviewLineItem,
  PreviewObjectRef,
  SyncPreviewResponse,
} from '../types/api';

interface SyncConfirmationDialogProps {
  open: boolean;
  dealId: string;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  isSyncing?: boolean;
}

/** Stripe amounts are minor units; formatCurrency takes major. */
const formatMinor = (minor: number | null | undefined, currency: string): string =>
  minor == null ? '—' : formatCurrency(minor / 100, currency);

const ActionBadge: React.FC<{ ref_: PreviewObjectRef }> = ({ ref_ }) => (
  <Badge
    variant={ref_.action === 'reuse' ? 'secondary' : 'default'}
    title={ref_.stripe_id ?? ref_.placeholder_id ?? undefined}
  >
    {ref_.action === 'reuse' ? 'Reused' : 'New'}
  </Badge>
);

/** A section that stays collapsed until the user asks for the detail. */
const Section: React.FC<{
  title: string;
  count?: number;
  children: React.ReactNode;
}> = ({ title, count, children }) => (
  <Collapsible className="border-t pt-3">
    <CollapsibleTrigger className="flex w-full items-center gap-1 text-sm font-medium hover:underline [&[data-state=open]>svg]:rotate-90">
      <ChevronRight className="h-4 w-4 transition-transform" />
      {title}
      {count !== undefined && (
        <span className="text-muted-foreground">({count})</span>
      )}
    </CollapsibleTrigger>
    <CollapsibleContent className="pt-3">{children}</CollapsibleContent>
  </Collapsible>
);

const LineItemRow: React.FC<{ item: PreviewLineItem }> = ({ item }) => (
  <TableRow>
    <TableCell className="font-medium">
      <span className="inline-flex items-center gap-2">
        {item.name ?? item.hubspot_line_item_id}
        {item.is_negative && (
          // The amounts on this row are already negative; the badge says why.
          <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">
            Credit
          </Badge>
        )}
      </span>
      {item.note && (
        <div className="text-xs text-muted-foreground">{item.note}</div>
      )}
    </TableCell>
    <TableCell>{item.quantity ?? '—'}</TableCell>
    <TableCell>{formatMinor(item.unit_amount_minor, item.currency)}</TableCell>
    <TableCell>
      {item.billing_type === 'recurring' && item.interval
        ? `Recurring / ${item.interval}`
        : item.billing_type === 'usage'
          ? 'Usage'
          : 'One-time'}
    </TableCell>
    <TableCell>
      <ActionBadge ref_={item.price} />
    </TableCell>
    <TableCell className="text-right">
      {item.discount && (
        <div className="text-xs text-muted-foreground">
          {item.discount.kind === 'percent'
            ? `${item.discount.percent_off}% off`
            : `${formatMinor(item.discount.amount_off_minor, item.currency)} off`}
        </div>
      )}
      {formatMinor(item.line_total_minor, item.currency)}
    </TableCell>
  </TableRow>
);

const PreviewSkeleton: React.FC = () => (
  <div className="space-y-3 py-2">
    <Skeleton className="h-5 w-2/3" />
    <Skeleton className="h-4 w-1/2" />
    <Skeleton className="h-4 w-1/3" />
    <Skeleton className="h-9 w-full" />
  </div>
);

const PreviewBody: React.FC<{ preview: SyncPreviewResponse }> = ({ preview }) => {
  const currency = preview.totals?.currency ?? 'usd';
  // Not every planned write creates an object: finalizing an invoice, tagging
  // a subscription, and sweeping a stale draft all change things that already
  // exist. Counting them as creations would overstate — and in the delete case
  // actively misdescribe — what is about to happen.
  const createCount = preview.planned_writes.filter(
    (w) => w.operation === 'create',
  ).length;
  const otherCount = preview.planned_writes.length - createCount;
  const blocking = preview.blockers.filter((b) => b.severity === 'blocking');
  const advisory = preview.blockers.filter((b) => b.severity === 'advisory');

  return (
    <div className="space-y-3 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium">{preview.source.deal_name}</span>
        <Badge variant="outline">
          {preview.source.type === 'quote' ? 'Quote' : 'Deal'}
        </Badge>
        {preview.billing && (
          <Badge variant="outline">
            {preview.billing.mode === 'subscription'
              ? 'Subscription'
              : 'One-off invoice'}
          </Badge>
        )}
      </div>

      {blocking.map((blocker) => (
        <Alert variant="destructive" key={blocker.code}>
          <AlertDescription>{blocker.message}</AlertDescription>
        </Alert>
      ))}
      {advisory.map((blocker) => (
        <Alert key={blocker.code}>
          <AlertDescription>{blocker.message}</AlertDescription>
        </Alert>
      ))}
      {/* Collapsed behind a "N warnings" summary, same as the deal-detail
          page, so a deal with several advisories doesn't push the actual
          preview — customer, line items, totals — below the fold. */}
      {preview.warnings.length > 0 && (
        <WarningsPanel warnings={preview.warnings} />
      )}

      {/* Always-visible summary. Everything else is one click away. */}
      {preview.customer && (
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground">Customer</span>
          <Badge
            variant={preview.customer.action === 'reuse' ? 'secondary' : 'default'}
          >
            {preview.customer.action === 'reuse' ? 'Reused' : 'New'}
          </Badge>
          <span>
            {preview.customer.name ?? preview.customer.email ?? 'New customer'}
          </span>
        </div>
      )}

      {preview.totals && (
        <div className="flex items-center justify-between font-medium">
          <span>Estimated first invoice</span>
          <span>
            {formatMinor(preview.totals.estimated_first_invoice_minor, currency)}
          </span>
        </div>
      )}

      {preview.planned_writes.length > 0 && (
        <div className="text-muted-foreground">
          {createCount > 0 && (
            <>
              {createCount} object{createCount === 1 ? '' : 's'} will be created
              in Stripe
            </>
          )}
          {createCount > 0 && otherCount > 0 && ', plus '}
          {otherCount > 0 && (
            <>
              {otherCount} change{otherCount === 1 ? '' : 's'} to existing
              object{otherCount === 1 ? '' : 's'}
            </>
          )}
        </div>
      )}

      {preview.customer && (
        <Section title="Customer detail">
          <div className="space-y-1 text-muted-foreground">
            {preview.customer.email && <div>{preview.customer.email}</div>}
            {preview.customer.matched_on && (
              <div>{preview.customer.matched_on}</div>
            )}
            {preview.customer.address_will_be_backfilled && (
              <div>Address will be added from HubSpot.</div>
            )}
            {preview.customer.tax_ids.map((taxId) => (
              <div key={`${taxId.type}-${taxId.value}`}>
                Tax ID {taxId.type}: {taxId.value} ({taxId.action.replace('_', ' ')})
              </div>
            ))}
          </div>
        </Section>
      )}

      {preview.line_items.length > 0 && (
        <Section title="Line items" count={preview.line_items.length}>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Item</TableHead>
                  <TableHead>Qty</TableHead>
                  <TableHead>Unit</TableHead>
                  <TableHead>Billing</TableHead>
                  <TableHead>Price</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {preview.line_items.map((item) => (
                  <LineItemRow key={item.hubspot_line_item_id} item={item} />
                ))}
              </TableBody>
            </Table>
          </div>
        </Section>
      )}

      {preview.billing && (
        <Section title="Dates & collection">
          <dl className="grid grid-cols-2 gap-1 text-muted-foreground">
            <dt>Billing starts</dt>
            <dd>
              {preview.billing.billing_start
                ? new Date(preview.billing.billing_start).toLocaleDateString()
                : 'On sync'}
            </dd>
            <dt>Collection</dt>
            <dd>{preview.billing.collection_method.replace(/_/g, ' ')}</dd>
            {preview.billing.days_until_due != null && (
              <>
                <dt>Due in</dt>
                <dd>{preview.billing.days_until_due} days</dd>
              </>
            )}
            {preview.billing.proration_behavior && (
              <>
                <dt>Proration</dt>
                <dd>{preview.billing.proration_behavior}</dd>
              </>
            )}
            <dt>Tax</dt>
            <dd>
              {preview.billing.automatic_tax
                ? 'Stripe Tax (calculated at invoice time)'
                // Manual mode says nothing about whether a rate was actually
                // resolved: with on_missing_tax_rate set to skip or warn, a
                // deal syncs with no rates at all. Read the lines rather than
                // implying HubSpot supplied one.
                : preview.line_items.some((item) => item.tax_rates.length > 0)
                  ? 'HubSpot tax rates'
                  : 'No tax rates — nothing will be charged'}
            </dd>
          </dl>
        </Section>
      )}

      {preview.planned_writes.length > 0 && (
        <Section
          title="Changes in Stripe"
          count={preview.planned_writes.length}
        >
          <ul className="space-y-1">
            {preview.planned_writes.map((write) => (
              <li key={write.order} className="flex items-center gap-2">
                <Badge
                  variant={write.operation === 'create' ? 'default' : 'secondary'}
                >
                  {write.operation}
                </Badge>
                <Badge variant="outline">{write.kind.replace(/_/g, ' ')}</Badge>
                <span className="text-muted-foreground">{write.label}</span>
              </li>
            ))}
          </ul>
        </Section>
      )}

      <Separator />
      <p className="text-xs text-muted-foreground">
        {preview.billing?.automatic_tax
          ? 'Estimate — tax is not included. Stripe Tax calculates it from the ' +
            "customer's address when the invoice is finalized, so the lines " +
            'above carry no tax rates by design. Stripe also computes proration ' +
            'and validates tax IDs when the objects are created.'
          : 'Estimate — Stripe computes final tax and proration, and validates ' +
            'tax IDs, when the objects are created.'}
      </p>
    </div>
  );
};

const SyncConfirmationDialog: React.FC<SyncConfirmationDialogProps> = ({
  open,
  dealId,
  onOpenChange,
  onConfirm,
  isSyncing = false,
}) => {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['sync-preview', dealId],
    queryFn: () => apiClient.getSyncPreview(dealId),
    enabled: open,
    // The plan must reflect HubSpot and Stripe as they are right now, and a
    // reopened dialog must never flash the previous deal's plan.
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
    retry: false,
  });

  // Proceed is gated only by a preview that came back with a real blocker.
  // A preview still loading — or one that failed to load — never blocks the
  // user: computing it creates nothing, so it is purely informational.
  const blockedByPreview = data?.can_sync === false;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Confirm sync
            {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
          </DialogTitle>
          <DialogDescription>
            {isLoading
              ? 'Working out exactly what will be created in Stripe…'
              : "Here's what syncing this deal will create in Stripe."}
          </DialogDescription>
        </DialogHeader>

        {isLoading && <PreviewSkeleton />}

        {isError && (
          <Alert variant="destructive">
            <AlertDescription className="flex items-center justify-between gap-2">
              <span>
                Couldn't build the preview. You can still proceed — the sync
                itself is unaffected.
              </span>
              <Button variant="outline" size="sm" onClick={() => refetch()}>
                Retry
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {data && <PreviewBody preview={data} />}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSyncing}
          >
            Cancel
          </Button>
          <Button onClick={onConfirm} disabled={isSyncing || blockedByPreview}>
            {isSyncing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isSyncing ? 'Syncing...' : 'Proceed'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SyncConfirmationDialog;
